import { ComponentChildren, ComponentProps, Ref, toChildArray } from 'preact';
import { useMemo } from 'preact/hooks';
import { forwardRef } from 'preact/compat';

import { CheckboxSetContext } from './CheckboxSetContext';
import { CheckboxRadioField } from '#PRIVATE_CheckboxRadioField';
import { InlineUserAssistance, UserAssistanceDensityType } from '#UNSAFE_UserAssistance';
import { useFormContext } from '#hooks/UNSAFE_useFormContext';
import { FormFieldContext } from '#hooks/UNSAFE_useFormFieldContext';
import { useFormFieldContextProps } from '#hooks/UNSAFE_useFormFieldContextProps';
import { FlexboxProps } from '#utils/UNSAFE_interpolations/flexbox';
import { Size } from '#utils/UNSAFE_size';
import { ValueUpdateDetail } from '#utils/UNSAFE_valueUpdateDetail';
import { FocusableHandle } from '#hooks/UNSAFE_useFocusableTextField';
import { styles } from './CheckboxSetStyles.css';
import { AriaAttributesSignalExcluded } from '#utils/UNSAFE_attributeUtils';
import { useId } from '#hooks/UNSAFE_useId';
import { LayoutColumnSpan } from '#utils/UNSAFE_styles/Layout';

type InlineUserAssistanceProps = ComponentProps<typeof InlineUserAssistance>;

type Props = {
  /**
   * The ID of an element (or space separated IDs of multiple elements) that
   * describes the input.
   */
  'aria-describedby'?: AriaAttributesSignalExcluded['aria-describedby'];
  /**
   * Text to provide guidance to help the user understand what data to enter.
   */
  assistiveText?: InlineUserAssistanceProps['assistiveText'];
  /**
   * set of CheckboxItems
   */
  children: ComponentChildren;
  /**
   * Specifies how many columns to span in a FormLayout with direction === 'row'
   */
  columnSpan?: LayoutColumnSpan;
  /**
   * layout direction of the children elements
   */
  direction?: FlexboxProps['direction'];
  /**
   * Help source URL associated with the component.
   */
  helpSourceLink?: InlineUserAssistanceProps['helpSourceLink'];
  /**
   * Custom text to be rendered for the <code>helpSourceLink</code>.
   */
  helpSourceText?: InlineUserAssistanceProps['helpSourceText'];
  /**
   * Specifies whether the checkbox set requires a selection to be made.
   */
  isRequired?: boolean;
  /**
   * Specifies if the checkbox set is read-only.
   */
  isReadonly?: boolean;
  /**
   * Specifies if the checkbox set is disabled.
   */
  isDisabled?: boolean;
  /**
   * Label text for the group of checkbox items.
   */
  label: string;
  /**
   * Specifies where the label is positioned relative to the field.
   */
  labelEdge?: 'none' | 'top' | 'start' | 'inside';
  /**
   * Specifies the width of the label when <code>labelEdge</code> is <code>"start"</code>.
   */
  labelStartWidth?: Size;
  /**
   * Messages to show on screen that are associated with the component.
   */
  messages?: InlineUserAssistanceProps['messages'];
  /**
   * Callback invoked each time the user changes selected option.
   */
  onCommit: (detail: ValueUpdateDetail<Set<string | number>>) => void;
  /**
   * Specifies the density of the user assistance presentation.  It can be set to:
   * <ul>
   * <li><code>'efficient'</code>: Show inline and reserve space to prevent layout reflow when user
   * assistance text is displayed.</li>
   * <li><code>'reflow'</code>: Show inline.  Layout will reflow when text is displayed.</li>
   * <li><code>'compact'</code>: Messages, help, hints, and required will not be shown inline; they will show in a mode that keeps the screen more compact, like a popup for the messages, and a required icon to indicate Required. </li>
   * </ul>
   */
  userAssistanceDensity?: UserAssistanceDensityType;
  /**
   * Value of the selected checkbox option.
   */
  value?: Set<string | number>;
};

export const CheckboxSet = forwardRef(
  (
    {
      'aria-describedby': ariaDescribedBy,
      assistiveText,
      columnSpan,
      direction = 'column',
      helpSourceLink,
      helpSourceText,
      isDisabled: propIsDisabled,
      isReadonly: propIsReadonly,
      isRequired = false,
      label,
      labelEdge: propLabelEdge,
      labelStartWidth: propLabelStartWidth,
      messages,
      onCommit,
      userAssistanceDensity: propUserAssistanceDensity,
      value,
      children
    }: Props,
    ref?: Ref<FocusableHandle>
  ) => {
    const hasValue = value !== undefined ? value.size > 0 : false;
    const baseId = useId();

    const {
      isDisabled: isFormDisabled,
      isReadonly: isFormReadonly,
      labelEdge: formLabelEdge,
      labelStartWidth: formLabelStartWidth,
      userAssistanceDensity: formUserAssistanceDensity
    } = useFormContext();

    // default to FormContext values if component properties are not specified
    const isDisabled = propIsDisabled ?? isFormDisabled;
    const isReadonly = propIsReadonly ?? isFormReadonly;
    const labelEdge = propLabelEdge ?? formLabelEdge;
    const labelStartWidth = propLabelStartWidth ?? formLabelStartWidth;
    const userAssistanceDensity = propUserAssistanceDensity ?? formUserAssistanceDensity;

    const formFieldContext = useFormFieldContextProps({
      hasValue,
      isDisabled,
      isReadonly
    });

    const checkboxSetContext = useMemo(
      () => ({
        name: baseId,
        value,
        onCommit
      }),
      [baseId, onCommit, value]
    );

    // wrap each CheckboxItem in an <li> tag and the entire set with <ul>
    const childrenArray = toChildArray(children).filter(Boolean);
    const wrappedChildren = childrenArray.map((item) => <li>{item}</li>);

    return (
      <FormFieldContext.Provider value={formFieldContext}>
        <CheckboxRadioField
          aria-describedby={ariaDescribedBy}
          assistiveText={assistiveText}
          baseId={baseId}
          columnSpan={columnSpan}
          direction={direction}
          helpSourceLink={helpSourceLink}
          helpSourceText={helpSourceText}
          isDisabled={isDisabled}
          isReadonly={isReadonly}
          isRequired={isRequired}
          label={label}
          labelEdge={labelEdge}
          labelStartWidth={labelStartWidth}
          parentComponentVariant="checkboxSet"
          messages={messages}
          userAssistanceDensity={userAssistanceDensity}
          hasValue={hasValue}
          role="group"
          ref={ref}>
          <CheckboxSetContext.Provider value={checkboxSetContext}>
            <ul className={styles.ul}>{wrappedChildren}</ul>
          </CheckboxSetContext.Provider>
        </CheckboxRadioField>
      </FormFieldContext.Provider>
    );
  }
);
